import Select from "react-select";
import CreatableSelect from "react-select/creatable";
import { SortableContainer } from "react-sortable-hoc";
import { ChevronDownIcon, ChevronUpIcon } from "@heroicons/react/outline";
import { SortableMultiValue, SortableMultiValueLabel } from "./components";
import Tooltip from "../Tooltip/Tooltip";

const customStyles = {
  control: (base: any) => ({
    ...base,
    fontSize: "0.875rem",
    borderRadius: "0.5rem",
  }),
  menuPortal: (base: any) => ({
    ...base,
    zIndex: 999,
  }),
  menu: (base: any) => ({
    ...base,
    fontSize: "0.875rem",
    zIndex: 999,
    background: "#ffffff",
  }),
  indicatorSeparator: () => ({
    display: "none",
  }),
  indicatorsContainer: () => ({
    display: "flex",
    alignItems: "center",
    marginRight: "8px",
  }),
  input: (base: any) => ({
    ...base,
    input: {
      ...base.input,
      outline: "none !important",
      boxShadow: "none !important",
    },
  }),
};

const DropdownIndicator = (props: any) => {
  return (
    <div className="opacity-50">
      {props.selectProps.menuIsOpen ? (
        <ChevronUpIcon {...props.innerProps} className="w-5 h-5" />
      ) : (
        <ChevronDownIcon {...props.innerProps} className="w-5 h-5" />
      )}
    </div>
  );
};

const SortableCreatable = SortableContainer(CreatableSelect);
const Sortable = SortableContainer(Select);

const getMultiItems = (options: any, selected = []) =>
  selected.map(
    (selectedItem: any) =>
      options.find((item: any) => selectedItem === item.value) ||
      (selectedItem?.value
        ? selectedItem
        : { value: selectedItem, label: selectedItem })
  );
const getSelectedValue = (options: any, selected: any) =>
  selected ? options.find((i: any) => i.value === selected) : null;

interface Props {
  label?: string;
  options?: any;
  value: any;
  onChange: any;
  name?: any;
  closeMenuOnSelect?: boolean;
  isLoading?: boolean;
  isSearchable?: boolean;
  isClearable?: boolean;
  disabled?: boolean;
  creatable?: boolean;
  isMulti?: boolean;
  noOptionsMessage?: any;
  tooltip?: string;
  type?: "default" | "tags";
  tooltipType?: "default" | "solid";
}

function FieldSelect({
  label,
  options = [],
  value,
  onChange,
  closeMenuOnSelect = false,
  isLoading = false,
  isSearchable = false,
  isClearable = true,
  disabled = false,
  creatable = false,
  isMulti,
  noOptionsMessage,
  tooltip,
  type = "default",
  tooltipType = "default",
}: Props) {
  const preparedValue = isMulti
    ? getMultiItems(options, value)
    : [getSelectedValue(options, value)];

  const selectProps = {
    styles: customStyles,
    options,
    noOptionsMessage,
    isClearable,
    openMenuOnClick: isMulti ? !preparedValue.length : true,
    value: preparedValue,
    placeholder: "",
    isSearchable: !!isSearchable,
    isDisabled: !!disabled,
    isLoading,
    isMulti,
    closeMenuOnSelect: !isMulti ? closeMenuOnSelect : true,
    onChange,
    components: { DropdownIndicator },
    getOptionLabel: (option: any) =>
      option.label || option.text || option.value || option,
    menuPortalTarget: document.body,
    menuPosition: "fixed",
  };

  const isTagsType = type === "tags";

  const arrayMove = (array: Array<any>, from: number, to: number) => {
    array = array.slice();
    array.splice(to < 0 ? array.length + to : to, 0, array.splice(from, 1)[0]);
    return array;
  };

  const onSortEnd = ({
    oldIndex,
    newIndex,
  }: {
    oldIndex: number;
    newIndex: number;
  }) => {
    const newValue = arrayMove(preparedValue, oldIndex, newIndex);
    onChange(newValue);
  };

  const sortableProps = {
    ...selectProps,
    distance: 1,
    axis: "xy",
    onSortEnd,
    components: {
      ...selectProps.components,
      MultiValue: SortableMultiValue,
      MultiValueLabel: SortableMultiValueLabel,
    },
  };

  const ariaFocusMessage = selectProps?.value || [];
  let focusElement = {};

  const onFocus = ({ focused }) => {
    focusElement = focused
      ? {
          value: focused.value,
          label: focused.value,
        }
      : {};
  };

  const onBlurSelect = () => {
    const isEmptyObject = Object.keys(focusElement).length === 0;
    if (!isEmptyObject) {
      ariaFocusMessage.push(focusElement);
      selectProps?.onChange(ariaFocusMessage);
    }
    focusElement = {};
  };

  const onMenuClose = () => {
    focusElement = {};
  };

  const handleInputChange = (str: string) => {
    if (isTagsType) {
      const regx = /[^\p{L}/0-9 ]/gu;
      const preparedStr = str.replace(regx, "");
      return preparedStr;
    }
  };

  return (
    <div>
      {label && (
        <h3 className="flex items-center text-sm font-medium text-gray-700">
          <span className="mr-1">{label}</span>
          <Tooltip tooltip={tooltip} type={tooltipType} />
        </h3>
      )}
      <div className="mt-1">
        {creatable ? (
          // @ts-ignore (axis validation error)
          <SortableCreatable
            {...selectProps}
            {...sortableProps}
            formatCreateLabel={(item) => item}
            ariaLiveMessages={{
              onFocus,
            }}
            onInputChange={handleInputChange}
            onMenuClose={onMenuClose}
            onBlur={onBlurSelect}
            backspaceRemovesValue={false}
          />
        ) : (
          // @ts-ignore (axis validation error)
          <Sortable
            ariaLiveMessages={{
              onFocus,
            }}
            onBlur={onBlurSelect}
            onMenuClose={onMenuClose}
            {...selectProps}
            {...sortableProps}
            backspaceRemovesValue={false}
          />
        )}
      </div>
    </div>
  );
}

export default FieldSelect;
